import { useQuery } from '@tanstack/react-query';
import { Card, Chip } from '@/components/kit';
import apiClient from '@/services/apiClient';
import type { WalletSummary } from '../lib/useManagerData';
import { money } from '../lib/scope';

/**
 * Message wallet, empty (f29) — the screen that decides the design.
 *
 * A wallet at zero is not an error state, it is an operating state: messages
 * stop, and the people who would have been messaged become a call list at the
 * desk. Saying "insufficient balance" and stopping would leave the clinic to
 * discover that by being phoned by a patient who never got their result.
 *
 * So this screen says three things in order: what stopped, who picked it up,
 * and what is still working without it.
 */

const unwrap = <T,>(body: unknown): T => ((body as { data?: T })?.data ?? body) as T;

/** "today 2:40 pm", or the day it happened when that was not today. */
function since(iso: string | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const time = d.toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true });
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  if (sameDay) return `today ${time}`;
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${time}`;
}

export function WalletEmpty({ wallet, onTopUp, onAuto }: { wallet: WalletSummary | null; onTopUp?: () => void; onAuto?: () => void }) {
  const currency = wallet?.currency?.toUpperCase() ?? 'EGP';

  /* WHO PICKED THE WORK UP. When messages pause, the desk is told in calls —
     the same count its own queue shows, read from the desk's day so the two
     screens cannot disagree about how many people are waiting for a phone
     call. A manager holds `appointment:read`, which is that endpoint's gate. */
  const { data: toCall } = useQuery({
    queryKey: ['reception-desk', 'day', null],
    staleTime: 30_000,
    retry: false,
    queryFn: async () => {
      const res = await apiClient.get('/reception-desk/day');
      return unwrap<{ toCall?: number }>(res.data)?.toCall ?? null;
    },
  });

  /* The movement that emptied it — the newest entry the summary carries. */
  const stoppedAt = since(wallet?.ledger?.[0]?.createdAt);
  const auto = wallet?.autoReload;

  return (
    <>
      <div className="cm-split" style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Card kind="danger">
            <p className="t-sec w6" style={{ color: 'var(--danger-600)' }}>MESSAGES ARE PAUSED</p>
            <p className="t-dxl" style={{ margin: '4px 0' }}>{money(0, currency)}</p>
            <p className="t-sec sec">
              {stoppedAt ? `Since ${stoppedAt}. ` : ''}
              Nothing has been lost — every message the clinic would have sent is waiting, and
              goes the moment the wallet is funded.
            </p>
            {onTopUp ? (
              <button type="button" className="btn btn-primary mt12" onClick={onTopUp}>Top up now</button>
            ) : null}
          </Card>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Card>
            <div className="flex" style={{ gap: 8, alignItems: 'flex-start' }}>
              <p className="t-strong grow">
                {toCall == null
                  ? 'Patients move to the desk’s call list'
                  : `${toCall} patient${toCall === 1 ? '' : 's'} moved to the desk’s call list`}
              </p>
              <Chip kind="warn" small>TO CALL</Chip>
            </div>
            <p className="t-cap mut mt8">
              a released result, a reminder, a recall — whatever could not be sent is a name the
              desk phones instead
            </p>
          </Card>
        </div>
      </div>

      <p className="t-ovl mut" style={{ marginTop: 18 }}>What is happening instead</p>
      <div className="cm-split" style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Card>
            <p className="t-ovl mut">Patients who message the clinic</p>
            <p className="t-sec sec mt4">still get answered</p>
            <p className="t-cap mut">
              replying to somebody who wrote to you costs the wallet nothing
            </p>
          </Card>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Card>
            <p className="t-ovl mut">Auto top-up</p>
            <p className="t-sec sec mt4">
              {auto?.enabled
                ? `on — below ${money((auto.thresholdMinor ?? 0) / 100, currency)} it adds ${money((auto.reloadAmountMinor ?? 0) / 100, currency)}`
                : 'was off'}
            </p>
            <p className="t-cap mut">
              {auto?.enabled
                ? auto.cardOnFile === false
                  ? 'no card is saved, so it fires, fails and emails the owner'
                  : 'the wallet refills itself before it empties'
                : 'turning it on is how a clinic stops reaching this screen'}
              {!auto?.enabled && onAuto ? (
                <> · <button type="button" className="brand w6" style={{ background: "none", border: 0, padding: 0, cursor: "pointer", font: "inherit" }} onClick={onAuto}>turn it on</button></>
              ) : null}
            </p>
          </Card>
        </div>
      </div>
      <div className="cm-split" style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Card>
            <p className="t-ovl mut">The portal</p>
            <p className="t-sec sec mt4">keeps working</p>
            <p className="t-cap mut">anyone holding a live link still opens their result</p>
          </Card>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Card kind="accent">
            <p className="t-sec brand">
              Running out of money must NEVER silently drop a clinical message. The queue falls back
              to the desk, and every message that could not send becomes a call task with a name on it.
            </p>
          </Card>
        </div>
      </div>
      <p className="t-sec mut" style={{ marginTop: 12 }}>
        The receptionist is never told the clinic is out of money — she is told there
        {toCall == null ? ' are people to call.' : toCall === 0 ? ' is no one to call.' : toCall === 1 ? ' is one person to call.' : ` are ${toCall} people to call.`}
      </p>
    </>
  );
}
